"use client";
import { useState, useEffect, useRef } from "react";
import { Link } from "@/i18n/navigation";
import { routeColorMap } from "@/utils/navColors";
import { arrowColor } from "@/utils/navColors";
import ArrowRightIcon from "@/assets/iconComponents/ArrowRightIcon";
import { useTranslations } from "next-intl";
import { usePathname } from "@/i18n/navigation";
import Image from "next/image";
import FebrikenRightArrow from "@/assets/iconComponents/FebrikenRightArrow";
import MobileMenuCrossIcon from "@/assets/iconComponents/MobileMenuCrossIcon";
import MenuIcon from "@/assets/iconComponents/MenuIcon";
import ButtonRightArrow from "@/assets/iconComponents/ButtonRightArrow";

export default function Navbar() {
  const t = useTranslations("menu");
  const pathname = usePathname();
  const [showBg, setShowBg] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const lastY = useRef(0);
  const [isFabrikenOpen, setIsFabrikenOpen] = useState(false);
  const [lang] = useState("sv");
  const [isSweOpen, setIsSweOpen] = useState(false);
  // const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isInkassoPage, setIsInkassoPage] = useState(false);
  const [isfakturaKopPage, setIsfakturaKopPage] = useState(false);
  const [isPurpleLinkColor, setIsPurpleLinkColor] = useState(false);
  const [currentArrowColor, setCurrentArrowColor] = useState("#B3594D");
  const [isSubMenuHovered, setIsSubMenuHovered] = useState(false);
  const [dropdownHeight, setDropdownHeight] = useState(0);
  const dropdownRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const fabrikenRef = useRef(null);

  // const handleManualChange = (newLang) => {
  //   setLang(newLang);
  //   setIsSweOpen(false);
  // };

  useEffect(() => {
    const onScroll = () => {
      const currentY = window.scrollY;
      const isScrollingUp = currentY > lastY.current || currentY < lastY.current;
      if (currentY <= 10) {
        setShowBg(false);
      } else if (isScrollingUp) {
        setShowBg(isScrollingUp);
      } else {
        setShowBg(false);
      }

      lastY.current = currentY;
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll(); // initialize
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [mobileMenuOpen]);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  useEffect(() => {
    setIsInkassoPage(pathname.includes("/inkasso"));
    setIsfakturaKopPage(pathname.includes("/fakturakop"));
  }, [pathname]);

  useEffect(() => {
    if (isfakturaKopPage || isInkassoPage) {
      setIsPurpleLinkColor(true);
    } else {
      setIsPurpleLinkColor(false);
    }
  }, [isfakturaKopPage, isInkassoPage]);

  const getNavBackgroundColor = () => {
    for (const route of routeColorMap) {
      if (route.match(pathname)) return route.color;
    }
    return "bg-[#B3594D]";
  };

  const getArrowColor = () => {
    for (const route of arrowColor) {
      if (route.match(pathname)) {
        setCurrentArrowColor(route.color);
        return;
      }
    }
    return setCurrentArrowColor("#B3594D");
  };

  useEffect(() => {
    getArrowColor();
  }, [pathname, getArrowColor]);

  const submenuHoverTimeout = useRef(null);
  const [canHover, setCanHover] = useState(false);

  // Detect devices with a real hover-capable pointer (laptops/desktops).
  // On touch devices (tablets/phones) hover is unreliable, so we rely on
  // click/tap instead to avoid the dropdown flashing open and closed.
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mql = window.matchMedia("(hover: hover) and (pointer: fine)");
    const update = () => setCanHover(mql.matches);
    update();
    mql.addEventListener("change", update);
    return () => mql.removeEventListener("change", update);
  }, []);

  const openSubMenu = () => {
    if (submenuHoverTimeout.current) clearTimeout(submenuHoverTimeout.current);
    setIsSubMenuHovered(true);
    setTimeout(() => {
      if (dropdownRef.current) {
        setDropdownHeight(dropdownRef.current.offsetHeight);
      }
    }, 0);
  };

  const closeSubMenu = () => {
    setIsSubMenuHovered(false);
  };

  // Hover handlers (laptops / devices with a real pointer only).
  const handleSubMenuEnter = () => {
    if (!canHover) return;
    openSubMenu();
  };

  const handleSubMenuLeave = () => {
    if (!canHover) return;
    submenuHoverTimeout.current = setTimeout(() => {
      setIsSubMenuHovered(false);
    }, 300);
  };

  // Click/tap toggle so the dropdown works on tablets (where hover is
  // unreliable) while still working on laptops.
  const handleFabrikenToggle = (e) => {
    e.preventDefault();
    if (submenuHoverTimeout.current) clearTimeout(submenuHoverTimeout.current);
    if (isSubMenuHovered) {
      closeSubMenu();
    } else {
      openSubMenu();
    }
  };

  // Close the desktop Fabriken dropdown when clicking/tapping outside of it.
  useEffect(() => {
    if (!isSubMenuHovered) return undefined;

    function handleFabrikenOutside(event) {
      if (fabrikenRef.current && !fabrikenRef.current.contains(event.target)) {
        closeSubMenu();
      }
    }

    document.addEventListener("mousedown", handleFabrikenOutside);
    document.addEventListener("touchstart", handleFabrikenOutside);

    return () => {
      document.removeEventListener("mousedown", handleFabrikenOutside);
      document.removeEventListener("touchstart", handleFabrikenOutside);
    };
  }, [isSubMenuHovered]);

  // Close the desktop Fabriken dropdown on route change.
  useEffect(() => {
    closeSubMenu();
  }, [pathname]);

  // const handleSweClick = () => {
  //   handleManualChange("sv");
  //   toggleMobileMenu();
  // };

  // const handleEngClick = () => {
  //   handleManualChange("en");
  //   toggleMobileMenu();
  // };

  useEffect(() => {
    function handleClickOutside(event) {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
        setMobileMenuOpen(false);
      }
    }

    if (mobileMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [mobileMenuOpen]);

  // Side-effects for mobile-menu <Link> clicks: close the menu and land at
  // the top. Navigation is handled by the <Link> itself (scroll={false}).
  const handleMobileLinkClick = () => {
    setMobileMenuOpen(false);
    window.scrollTo({ top: 0, left: 0 });
  };
  return (
    <>
      <nav
        className={`
          fixed top-0 left-0 w-full z-[70]
          transition-all duration-300 ease-in-out sm:p-0
          ${showBg || mobileMenuOpen || isSubMenuHovered ? getNavBackgroundColor() : "bg-transparent"}
          px-[clamp(16px,6vw,26px)] py-4 sm:px-8 sm:py-4
          lg:py-8 lg:px-10
          xl:py-8 xl:px-16
          2xl:py-14 2xl:px-16
           transition-all duration-300
        `}
        style={isSubMenuHovered ? { paddingBottom: `${dropdownHeight + 16}px` } : {}}
      >
        <div
          className={`flex justify-between z-45 items-center w-full font-[400] text-22px ${
            isPurpleLinkColor ? "text-[#5C2483]" : "text-[#EDD2B9]"
          }`}
        >
          <Link href="/">
            <Image
              src={isPurpleLinkColor ? "/Icons/purpleLogo.svg" : "/Icons/Finansfabriken.svg"}
              width={192}
              height={40}
              className="w-[clamp(100px,9.635vw,200px)]"
              alt="Finansfabriken"
              priority
            />
          </Link>

          <ul className="hidden md:flex gap-8 2xl:space-x-14 xl:space-x-12">
            {[
              { to: "/foretagslan", label: t("menu-1") },
              { to: "/fakturakop", label: t("menu-3") },
              { to: "/inkasso", label: t("menu-2") },
              { to: "/fabrikens-vanner", label: t("menu-4-option.menu-4-option-5") },

              {
                to: "/",
                label: (
                  <div className="flex items-center justify-center gap-2">
                    <span className="flex items-center gap-1">{t("menu-4")}</span>

                    {/* The <img> tag is replaced by a <div> wrapping the icon component */}
                    <div className="w-3 h-3 ml-1 group-hover:rotate-90 duration-300">
                      <ButtonRightArrow
                        size={12}
                        color={isPurpleLinkColor ? "#5C2483" : "#EDD2B9"}
                      />
                    </div>
                  </div>
                ),
                dropdown: [
                  {
                    to: "/aboutus",
                    label: t("menu-4-option.menu-4-option-1"),
                    children: [
                      {
                        to: "/medarbetare",
                        label: t("menu-4-option.menu-4-option-1-sub-1")
                      },
                      {
                        to: "/styrelse",
                        label: t("menu-4-option.menu-4-option-1-sub-2")
                      },
                      {
                        to: "/agare",
                        label: t("menu-4-option.menu-4-option-1-sub-3")
                      }
                    ]
                  },
                  {
                    to: "/csr",
                    label: t("menu-4-option.menu-4-option-6")
                  },
                  {
                    to: "/blog",
                    label: t("menu-4-option.menu-4-option-2")
                  },
                  {
                    to: "/faq",
                    label: t("menu-4-option.menu-4-option-3")
                  }
                ]
              }
            ].map(({ to, label, dropdown }) => (
              <li key={label} className="group relative" ref={dropdown ? fabrikenRef : undefined}>
                {dropdown ? (
                  <button
                    type="button"
                    onMouseEnter={handleSubMenuEnter}
                    onMouseLeave={handleSubMenuLeave}
                    onClick={handleFabrikenToggle}
                    aria-expanded={isSubMenuHovered}
                    aria-haspopup="true"
                    className={`relative inline-block cursor-pointer ${
                      isPurpleLinkColor
                        ? "text-[#5C2483] hover:text-[#5C2483]/80"
                        : "text-[#EDD2B9] hover:text-[#EDD2B9]/80"
                    } transition-colors duration-100 `}
                  >
                    {label}
                    <span
                      className={`
             left-0 bottom-0 block h-[1px] w-full
            bg-current
            origin-left
            transition-transform duration-300
            ${isSubMenuHovered ? "scale-x-100" : "scale-x-0"}
          `}
                    />
                  </button>
                ) : (
                  <Link
                    href={to}
                    className={`relative inline-block ${
                      isPurpleLinkColor
                        ? "text-[#5C2483] hover:text-[#5C2483]/80"
                        : "text-[#EDD2B9] hover:text-[#EDD2B9]/80"
                    } transition-colors duration-100`}
                  >
                    {label}
                    <span
                      className="left-0 bottom-0 block h-[1px] w-full  scale-x-0 origin-left transition-transform duration-300 group-hover:scale-x-100"
                      style={{
                        backgroundColor: isPurpleLinkColor ? "#5C2483cc" : "#EDD2B9cc"
                      }}
                    />
                  </Link>
                )}

                {/* Dropdown menu if exists */}
                {dropdown && (
                  <ul
                    ref={dropdownRef}
                    onMouseEnter={handleSubMenuEnter}
                    onMouseLeave={handleSubMenuLeave}
                    className={`absolute top-full left-0 lg:mt-2 2xl:mt-4 rounded-lg transition-all duration-200 min-w-[10rem] ${
                      isSubMenuHovered ? "opacity-100 visible" : "opacity-0 invisible"
                    }`}
                  >
                    {dropdown.map((item) => (
                      <li key={item.label}>
                        <Link
                          href={item.to}
                          scroll={false}
                          onClick={() => {
                            window.scrollTo({ top: 0, left: 0 });
                            closeSubMenu();
                          }}
                          className={`block mt-2 desktop-text-16px whitespace-nowrap ${
                            isPurpleLinkColor
                              ? "text-[#5C2483] hover:text-[#5C2483]/80"
                              : "text-[#EDD2B9] hover:text-[#EDD2B9]/80"
                          }`}
                        >
                          {item.label}
                        </Link>
                        {item.children && (
                          <ul className="pl-4">
                            {item.children.map((child) => (
                              <li key={child.label}>
                                <Link
                                  href={child.to}
                                  scroll={false}
                                  onClick={() => {
                                    window.scrollTo({ top: 0, left: 0 });
                                    closeSubMenu();
                                  }}
                                  className={`block mt-1.5 desktop-text-16px whitespace-nowrap ${
                                    isPurpleLinkColor
                                      ? "text-[#5C2483] hover:text-[#5C2483]/80"
                                      : "text-[#EDD2B9] hover:text-[#EDD2B9]/80"
                                  }`}
                                >
                                  {child.label}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>

          <div
            className="hidden md:flex items-center gap-4 xl:gap-12"
            style={{ visibility: "hidden" }}
          >
            <div className="relative inline-block group" style={{ visibility: "hidden" }}>
              <div className="flex justify-center items-center">
                <button
                  onClick={() => setIsSweOpen((o) => !o)}
                  className="
                  relative py-1 px-1 rounded-lg flex items-center gap-4
                  transition-colors duration-300
                "
                >
                  {lang === "sv" ? "Swe" : "Eng"}
                  <span
                    className={`
                    absolute left-0 bottom-0 h-[1px] w-full bg-current
                    origin-left transition-transform duration-300
                    ${isSweOpen ? "scale-x-100" : "scale-x-0"}
                    group-hover:scale-x-100
                  `}
                  />
                </button>
                <div
                  className={`w-3 h-3 ml-1
    transition-transform duration-300
    ${isSweOpen ? "rotate-90" : "rotate-0"}
    group-hover:rotate-90
  `}
                >
                  <ButtonRightArrow size={12} color={isPurpleLinkColor ? "#5C2483" : "#EDD2B9"} />
                </div>
              </div>
              <div
                className={`
      absolute top-full left-0 mt-1 w-28
      overflow-hidden z-10

      ${isSweOpen ? "block" : "hidden"}
      group-hover:block
    `}
              >
                <button
                  onClick={() => handleManualChange(lang === "sv" ? "en" : "sv")}
                  className="block w-full text-left py-2 px-1 cursor-pointer"
                >
                  {lang === "sv" ? "Eng" : "Swe"}
                </button>
              </div>
            </div>
            <button
              className={`py-1 px-3 rounded-full ${
                isPurpleLinkColor ? "bg-[#5C2483]" : "bg-[#EDD2B9]"
              }  transition-colors duration-300 flex items-center gap-2 text-20px-normal`}
            >
              <span style={{ color: currentArrowColor }}>{t("login")}</span>{" "}
              <ArrowRightIcon fill={currentArrowColor} />
            </button>
          </div>
          <div className="md:hidden flex items-center gap-4">
            <button onClick={toggleMobileMenu} className="focus:outline-none">
              {mobileMenuOpen ? (
                <MobileMenuCrossIcon
                  color={isPurpleLinkColor ? "#5C2483" : "#EDD2B9"}
                  size={24}
                  className="w-5 h-5"
                />
              ) : (
                <MenuIcon color={isPurpleLinkColor ? "#5C2483" : "#EDD2B9"} />
              )}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <div
        ref={mobileMenuRef}
        className={`
          flex justify-between
          fixed top-0 left-0 w-full  z-[65]
          transition-transform duration-300 ease-in-out
          ${mobileMenuOpen ? "translate-y-0" : "-translate-y-full"}
          pt-18 pb-8 px-6
          ${mobileMenuOpen ? getNavBackgroundColor() : "bg-transparent"}
           overflow-y-auto max-h-screen
        `}
      >
        <div
          className={`flex w-[60%] flex-col mobile-body-copy-text leading-[150%] ${
            isPurpleLinkColor ? "text-[#5C2483]" : "text-[#EDD2B9]"
          }`}
        >
          <Link
            href="/foretagslan"
            scroll={false}
            className="block"
            onClick={handleMobileLinkClick}
          >
            {t("menu-1")}
          </Link>
          <Link href="/fakturakop" scroll={false} className="block" onClick={handleMobileLinkClick}>
            {t("menu-3")}
          </Link>
          <Link href="/inkasso" scroll={false} className="block" onClick={handleMobileLinkClick}>
            {t("menu-2")}
          </Link>
          <Link
            href="/fabrikens-vanner"
            scroll={false}
            className="block"
            onClick={handleMobileLinkClick}
          >
            {t("menu-4-option.menu-4-option-5")}
          </Link>
          <div>
            <button
              onClick={() => setIsFabrikenOpen((open) => !open)}
              className="w-fit flex gap-2 items-center rounded-lg transition-colors duration-300"
            >
              <span>{t("menu-4")}</span>
              <FebrikenRightArrow
                fill={isPurpleLinkColor ? "#5C2483" : "#EDD2B9"}
                className={`transition-transform w-[3.25vw] h-auto ${
                  isFabrikenOpen ? "rotate-90" : ""
                }`}
              />
            </button>

            {/* sub-list for Fabriken */}
            {isFabrikenOpen && (
              <div className="flex flex-col gap-1">
                <div>
                  <Link
                    href="/aboutus"
                    scroll={false}
                    className="block"
                    onClick={handleMobileLinkClick}
                  >
                    {t("menu-4-option.menu-4-option-1")}
                  </Link>
                  <div className="mobile-text-16px leading-[145%] pl-4 flex flex-col gap-1">
                    <Link
                      href="/medarbetare"
                      scroll={false}
                      className="block"
                      onClick={handleMobileLinkClick}
                    >
                      {t("menu-4-option.menu-4-option-1-sub-1")}
                    </Link>
                    <Link
                      href="/styrelse"
                      scroll={false}
                      className="block"
                      onClick={handleMobileLinkClick}
                    >
                      {t("menu-4-option.menu-4-option-1-sub-2")}
                    </Link>
                    <Link
                      href="/agare"
                      scroll={false}
                      className="block"
                      onClick={handleMobileLinkClick}
                    >
                      {t("menu-4-option.menu-4-option-1-sub-3")}
                    </Link>
                  </div>
                </div>
                <Link href="/csr" scroll={false} className="block" onClick={handleMobileLinkClick}>
                  {t("menu-4-option.menu-4-option-6")}
                </Link>
                <Link href="/blog" scroll={false} className="block" onClick={handleMobileLinkClick}>
                  {t("menu-4-option.menu-4-option-2")}
                </Link>
                <Link href="/faq" scroll={false} className="block" onClick={handleMobileLinkClick}>
                  {t("menu-4-option.menu-4-option-3")}
                </Link>
              </div>
            )}
          </div>
          {/* <li className="mt-7.5">
            <button
              className={`py-1 px-3 rounded-full ${
                isPurpleLinkColor ? "bg-[#5C2483]" : "bg-[#EDD2B9]"
              }  transition-colors duration-300 flex items-center gap-2 text-20px-normal`}
            >
              <span style={{ color: currentArrowColor }}>
                {t("menu.login")}
              </span>{" "}
              <ArrowRightIcon fill={currentArrowColor} />
            </button>
          </li> */}
        </div>
        {/* <div
          className={`flex mobile-body-copy-text justify-end gap-2 ${
            isPurpleLinkColor ? "text-[#5C2483]" : "text-[#EDD2B9]"
          }`}
        >
          <button className="flex items-start" onClick={handleSweClick}>
            Swe
          </button>
          <div>/</div>
          <button className="flex items-start" onClick={handleEngClick}>
            Eng
          </button>
        </div> */}
      </div>
    </>
  );
}
